import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  MemoriesResponse,
  Memory,
  MemoryRequest,
  MemoryResponse,
  MemoryUpdateRequest,
} from '#models/db/memory/memory.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { IMemoriesService } from './memories.interface';
import { logger } from '#lib/logger';

@Injectable({
  providedIn: 'root',
})
export class MemoriesService implements IMemoriesService {
  path = 'memories';
  memoryAssetsPath = 'memory-assets';

  createMemoryLoading$ = new Subject<boolean>();
  getMemoryLoading$ = new Subject<boolean>();
  updateMemoryLoading$ = new Subject<boolean>();
  deleteMemoryLoading$ = new Subject<boolean>();
  getMemoriesLoading$ = new Subject<boolean>();

  activeMemory$ = new BehaviorSubject<Memory | null>(null);
  memories$ = new BehaviorSubject<Memory[] | null>(null);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  async createMemory(request: MemoryRequest, nextFn?: (data: Memory | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.createMemoryLoading$,
        subject: this.activeMemory$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<MemoryResponse, MemoryRequest>(
          {
            url: this.path,
          },
          request
        );

        logger.log(response.details, 'MemoriesService.createMemory -> response');

        return response.details;
      }
    );
  }

  setActiveMemory(memory: Memory | null) {
    logger.log(memory, 'MemoriesService.setActiveMemory -> memory');
    this.activeMemory$.next(memory);
  }

  async getMemories(nextFn?: (data: Memory[] | null) => void, silent?: boolean) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getMemoriesLoading$ : undefined,
        subject: this.memories$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<MemoriesResponse>({
          url: this.path,
        });

        logger.log(response.details, 'MemoriesService.getMemories -> response');

        return response.details;
      }
    );
  }

  async getMemoryById(id: string, nextFn?: (data: Memory | null) => void, silent?: boolean) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getMemoryLoading$ : undefined,
        subject: this.activeMemory$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<MemoryResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response.details, 'MemoriesService.getMemoryById -> response');

        return response.details;
      }
    );
  }

  async updateMemory(request: MemoryUpdateRequest, nextFn?: (data: Memory | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updateMemoryLoading$,
        subject: this.activeMemory$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<MemoryResponse, MemoryUpdateRequest>(
          {
            url: `${this.path}/${request.id}`,
          },
          request
        );

        logger.log(response.details, 'MemoriesService.updateMemory -> response');

        return response.details;
      }
    );
  }

  async deleteMemory(id: string, nextFn?: (data: Memory | null) => void): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.deleteMemoryLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.delete<MemoryResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response.details, 'MemoriesService.deleteMemory -> response');

        return response.details;
      }
    );
  }

  async publishMemory(id: string, nextFn?: (data: Memory | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updateMemoryLoading$,
        subject: this.activeMemory$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.put<MemoryResponse, null>(
          {
            url: `${this.path}/${id}/publish`,
          },
          null
        );

        logger.log(response.details, 'MemoriesService.publishMemory -> response');

        return response.details;
      }
    );
  }

  resetMemories() {
    this.memories$.next(null);
    this.activeMemory$.next(null);
    logger.log('complete', 'MemoriesService.resetMemories');
  }
}
